/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AppConfig } from '../../../AppConfig';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { Controller } from '../../../common/Controller';
import { AppHelper } from '../../../helpers/AppHelper';
import { PageDialog } from '../../../common/components/PageDialog';
import { SearchBar } from '../../../common/components/SearchBar';
import { AppStyles } from '../../../styles/AppTheme';

const WA_VENDOR_PROJECTS_BRIDGE = 'whatsappvendorprojects'
const WA_POLL_INTERVAL_MS = 5000
const EXCLUDED_EDIT_KEYS = ['waOutgoingSubOrgIds', 'ctcOutgoingSubOrgIds']
const EXCLUDED_EDIT_LABELS = ['Teams WhatsApp Number', 'Teams Click to Call Number']

function isExcludedEditField(field) {
  if (!field) return false
  if (EXCLUDED_EDIT_KEYS.includes(field.key)) return true
  if (EXCLUDED_EDIT_LABELS.includes(field.label)) return true
  return false
}

function normalizeVendorProjectsResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  return []
}

async function getVendorProjectById(ms, orgId, vendorProjectId) {
  const response = await ApiHelper.callAwait({
    ms,
    method: 'GET',
    paths: ['hooks'],
    queryParams: { orgId, bridgePath: WA_VENDOR_PROJECTS_BRIDGE },
  })
  const items = normalizeVendorProjectsResponse(response)
  return items.find(item => item.id === vendorProjectId) || null
}

function hasWhatsAppProvider(providers) {
  return (providers || []).some(p => String(p).toUpperCase() === 'WHATSAPP')
}

function getApiErrorMessage(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  if (!('respCode' in data) && !('message' in data)) return null
  if (data.respCode === 0) return null
  return data.message ? String(data.message).trim() : Helper.getString('customerCareErr')
}

function resolveOutgoingTeamLabels(fieldDef, fieldKey, ids) {
  if (!ids || ids.length === 0) return ''
  const field = fieldDef && Helper.getField(fieldDef, fieldKey)
  const options = (field && field.options) || []
  return ids.map(soId => {
    const soOption = options.find(o => o.key === soId)
    return soOption ? soOption.label : soId
  }).join(', ')
}

function WaPollingDialogContent() {
  return (
    <div className={css(Styles.waPollingContent)}>
      <CircularProgress />
      <span>Waiting for connection ...</span>
    </div>
  )
}

export class CrmNumbers extends BaseEntityPageSlixta {
  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'Numbers'
    this.pageKey = 'crm-numbers'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.pollActive = false
    this.waPollTimerId = null
    this.currentWaVendorProjectId = null
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId, pb: 'fieldDef' },
      },
    ]
    this.apiData = {
      orgId: this.orgId,
    }
    this.tableHeadersMap = {
      Title: current => (
        <span className={css(Styles.line1)}>{current.title || '—'}</span>
      ),
      Phone: current => (
        <span className={css(Styles.line1)}>{current.phone || '—'}</span>
      ),
      Providers: current => (
        <>
          <div>
            {(current.providers || []).map((provider, i) => (
              <span key={i} className={css(Styles.providerChip)}>{provider}</span>
            ))}
          </div>
        </>
      ),
      Outbound: current => {
        const waOutgoingTeams = resolveOutgoingTeamLabels(this.fieldDef, 'waOutgoingSubOrgIds', current.waOutgoingSubOrgIds)
        const ctcOutgoingTeams = resolveOutgoingTeamLabels(this.fieldDef, 'ctcOutgoingSubOrgIds', current.ctcOutgoingSubOrgIds)
        const showWaDefault = current.primaryWaNumber === 'YES'
        const showCtcDefault = current.primaryCtcNumber === 'YES'
        return <>
          {(showWaDefault || showCtcDefault) && (
            <div>
              {showWaDefault && (
                <span key={'primary-wa-number'} className={css(Styles.providerChip)}>{'WHATSAPP ACCOUNT DEFAULT'}</span>
              )}
              {showCtcDefault && (
                <span key={'primary-ctc-number'} className={css(Styles.providerChip)}>{'CTC ACCOUNT DEFAULT'}</span>
              )}
            </div>
          )}
          {waOutgoingTeams.length > 0 && (
            <div className={css(Styles.teamSection)}>
              <div className={css(Styles.line2)}><strong>WhatsApp Teams</strong></div>
              <span className={css(Styles.line2)}>{waOutgoingTeams}</span>
            </div>
          )}
          {ctcOutgoingTeams.length > 0 && (
            <div className={css(Styles.teamSection)}>
              <div className={css(Styles.line2)}><strong>CTC Teams</strong></div>
              <span className={css(Styles.line2)}>{ctcOutgoingTeams}</span>
            </div>
          )}
        </>
      },
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = []
    this.headerActions.push({
      label: 'Add new',
      icon: 'add',
      onClick: () => this.openModalForAdd(),
    })
    this.headerActions.push({
      label: 'Refresh',
      tooltip: 'Refresh',
      icon: 'refresh',
      type: 'icon-label',
      onClick: () => this.fetchItems(),
    })
    this.state = {
      ...this.state,
      masterNumberList: [],
      searchText: '',
      showWaPollingDialog: false,
      waTabOpened: false,
      pollActive: false,
    }
  }

  componentWillUnmount() {
    this.abortWaPolling()
  }

  async onFetchSuccess(results) {
    const crmnumbers = this.getResult(results, 'crmnumbers')
    const items = (crmnumbers && crmnumbers.items) || []
    if (crmnumbers && crmnumbers.fieldDef) {
      this.fieldDef = this.formDefinition = crmnumbers.fieldDef
    }
    this.setState({ masterNumberList: items }, () => {
      this.onSearch(this.state.searchText)
    })
  }

  openModalForAdd() {
    const fieldDef = JSON.parse(JSON.stringify(this.fieldDef))
    if (fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields) {
      fieldDef.steps[0].fields = fieldDef.steps[0].fields.filter(
        f => f.key === 'title' || f.key === 'phone'
      )
    }
    super.openModalForAdd(fieldDef)
  }

  onAdd() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crmnumbers'], queryParams: { orgId: this.orgId } }),
    })
  }

  openModalForEdit(current) {
    const fieldDef = JSON.parse(JSON.stringify(this.fieldDef))
    if (fieldDef && Array.isArray(fieldDef.steps)) {
      fieldDef.steps.forEach(step => {
        if (step && Array.isArray(step.fields)) {
          step.fields = step.fields.filter(f => !isExcludedEditField(f))
        }
      })
    }
    const providersField = Helper.getField(fieldDef, 'providers')
    if (providersField) providersField.disabled = true
    const phoneField = Helper.getField(fieldDef, 'phone')
    if (phoneField) phoneField.disabled = !!(current.providers && current.providers.length > 0)
    super.openModalForEdit(current, fieldDef)
  }

  onEdit(current) {
    this.setState({
      apiMethod: 'PUT',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crmnumbers', current.id], queryParams: { orgId: this.orgId } }),
    })
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  onSearch(searchText = '') {
    const search = String(searchText || '').trim().toLowerCase()
    if (!search) {
      this.setState({
        items: this.state.masterNumberList,
        searchText,
      })
      return
    }
    const items = (this.state.masterNumberList || []).filter(row =>
      String(row.title || '').toLowerCase().includes(search) ||
      String(row.phone || '').toLowerCase().includes(search) ||
      (Array.isArray(row.providers) ? row.providers.join(' ') : String(row.providers || '')).toLowerCase().includes(search)
    )
    this.setState({
      items,
      searchText,
    })
  }

  clearSearch() {
    this.setState({
      items: this.state.masterNumberList || [],
      searchText: '',
    })
  }

  shouldShowWaConnectButton() {
    return AppHelper.isSettingEnabled(this.orgId, 'CRM_NUMBER_CONNECT_WHATSAPP')
  }

  onActions = (current) => {
    const rowIcons = []
    if (!hasWhatsAppProvider(current.providers) && this.shouldShowWaConnectButton()) {
      rowIcons.push(
        <Tooltip title='Connect to WhatsApp' placement='top-start' key={`${current.id}-wa`}>
          <span
            className={css(AppStyles.entityTableLinkIcon, Styles.rowActionIconWrapper)}
            onClick={() => this.startWaOnboarding(current)}
          >
            <i className='material-icons-outlined'>add_link</i>
          </span>
        </Tooltip>
      )
    }
    rowIcons.push(
      <Tooltip title='Edit' placement='top-start' key={`${current.id}-editTT`}>
        <span className={css(AppStyles.entityTableLinkIcon, Styles.rowActionIconWrapper)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForEdit(current)}>edit</i>
        </span>
      </Tooltip>
    )
    return [
      <span className={css(Styles.rowActions)} key={`${current.id}-actions`}>
        {rowIcons}
      </span>,
    ]
  }

  startWaOnboarding(current) {
    if (this.pollActive) return
    this.currentWaVendorProjectId = null
    this.pollActive = true
    this.setState({
      showWaPollingDialog: true,
      waTabOpened: false,
      pollActive: true,
    }, () => this.pollWhatsappOnboarding(current.phone, current.id))
  }

  abortWaPolling() {
    this.pollActive = false
    this.currentWaVendorProjectId = null
    if (this.waPollTimerId) {
      clearTimeout(this.waPollTimerId)
      this.waPollTimerId = null
    }
  }

  closeWaPollingDialog() {
    this.abortWaPolling()
    this.setState({
      showWaPollingDialog: false,
      pollActive: false,
    })
  }

  stopWaPolling({ isError, message, refresh }) {
    this.abortWaPolling()
    this.setState({
      showWaPollingDialog: false,
      pollActive: false,
      showToast: !!(message && isError),
      isErrorToast: !!isError,
      toastMsg: message,
    })
    if (refresh) this.fetchItems()
  }

  async pollWhatsappOnboarding(number, rowId) {
    if (!this.pollActive) return

    try {
      if (!this.currentWaVendorProjectId) {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'whatsapp-onboard-number'],
          queryParams: { orgId: this.orgId },
          jsonBody: { number },
        })
        const apiError = getApiErrorMessage(response)
        if (apiError) {
          this.stopWaPolling({ isError: true, message: apiError })
          return
        }
        const signupUrl = response && response.signupResponse && response.signupResponse.embeddedSignupURL
        if (signupUrl && !this.state.waTabOpened) {
          window.open(String(signupUrl).trim(), '_blank', 'noopener,noreferrer')
          this.setState({ waTabOpened: true })
        }
        const providers = response && response.crmNumber && response.crmNumber.providers
        if (hasWhatsAppProvider(providers)) {
          this.stopWaPolling({ refresh: true })
          return
        }
        const waVendorProjectId = response && response.crmNumber && response.crmNumber.waVendorProjectId
        if (waVendorProjectId) {
          this.currentWaVendorProjectId = waVendorProjectId
        }
      } else {
        const item = await getVendorProjectById(this.ms, this.orgId, this.currentWaVendorProjectId)
        if (item && String(item.status).toUpperCase() === 'LIVE') {
          this.currentWaVendorProjectId = null
        }
      }
      if (this.pollActive) {
        this.waPollTimerId = setTimeout(() => this.pollWhatsappOnboarding(number, rowId), WA_POLL_INTERVAL_MS)
      }
    } catch (e) {
      this.stopWaPolling({ isError: true, message: Helper.getErrorMsg(e) })
    }
  }

  renderAboveTable() {
    return (
      <SearchBar
        hint='Search by Title, Phone, or Provider'
        onSearch={this.onSearch.bind(this)}
        onClearSearch={this.clearSearch.bind(this)}
        searchText={this.state.searchText}
        searchButtonLabel='Search'
        clearSearchLabel='Clear Search'
        autoRefresh
        debounceDuration={1}
        autoFocus={true}
      />
    );
  }

  renderBelowTable() {
    return (
      <PageDialog
        isOpen={this.state.showWaPollingDialog}
        title='Connect to WhatsApp'
        onClosed={() => this.closeWaPollingDialog()}
        fullWidth
        maxWidth='sm'
        PageComponent={WaPollingDialogContent}
      />
    )
  }
}

const Styles = StyleSheet.create({
  line1: {
    fontSize: 16,
  },
  line2: {
    fontSize: 12,
  },
  teamSection: {
    marginTop: 4,
  },
  providerChip: {
    display: 'inline-block',
    backgroundColor: '#e0e0e0',
    padding: '2px 8px',
    borderRadius: 4,
    marginRight: 4,
    fontSize: 12,
  },
  waPollingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: 24,
    minWidth: 240,
  },
  rowActions: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  rowActionIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    height: 28,
    width: 28,
  },
  waConnectIcon: {
    fontSize: 24,
    width: 24,
    height: 24,
    display: 'block',
  },
})
